/**
 * Recovery, trust and key rotation contracts (docs/e2e.md "Join, PRF, recovery and revocation").
 * Additive to content-directory.ts. The relay stores and returns only public keys, wrapped keys,
 * escrow ciphertext, ids, bounded enums, counters and timestamps; endpoints verify everything.
 */
import { z } from 'zod';
import { ContentBase64Url } from './content.js';
import { ContentDirectoryPage, ContentKeyWrap, ContentPublicKey } from './content-directory.js';
import { DeviceEndorsement, WebAuthnAssertion } from './signing.js';
const Id = z.string().min(1).max(256);
const Counter = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Time = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Digest = ContentBase64Url.refine((s) => s.length === 43);
export const EscrowKind = z.enum(['recovery', 'prf']);
/** `@viko/crypto` DirectoryEscrow: escrowed recipient private key sealed under a code/PRF KEK. */
export const DirectoryEscrowRecord = z.strictObject({
    version: z.literal(1),
    accountId: Id,
    kind: EscrowKind,
    recipientId: Id,
    publicKey: ContentPublicKey,
    escrow: z.strictObject({
        v: z.literal(1),
        accountId: Id,
        kind: EscrowKind,
        recipientId: Digest,
        credentialId: Id.optional(),
        box: z.strictObject({
            version: z.literal(1),
            algorithm: z.literal('A256GCM'),
            iv: ContentBase64Url.refine((s) => s.length === 16),
            ciphertext: ContentBase64Url.max(64_000),
        }),
    }),
    /** PRF escrows opened with the constant v2 salt (single-ceremony sign-in); additive. */
    prfSalt: z.literal(2).optional(),
});
export const EscrowEpochWrap = z.strictObject({ epoch: Counter, wrap: ContentKeyWrap });
/** Publish or replace one escrow, with wraps of every historical epoch the publisher holds. */
export const ContentEscrowPublish = z.strictObject({
    escrow: DirectoryEscrowRecord,
    wraps: z.array(EscrowEpochWrap).min(1).max(4096),
});
/** `@viko/crypto` SignedRevocationList. */
export const SignedRevocationListSchema = z.strictObject({
    list: z.strictObject({
        v: z.literal(1),
        type: z.literal('viko.revocations'),
        seq: Counter,
        issuedAt: Time,
        devices: z.array(Id).max(1024),
        credentials: z.array(Id).max(1024),
    }),
    assertion: WebAuthnAssertion.strict(),
});
/** Opt-in catch-up read: `ContentDirectoryRead` plus `catchUp: true`. */
export const ContentDirectoryCatchUpRead = z.strictObject({
    afterRevision: Count,
    recipientId: Id,
    catchUp: z.literal(true),
});
export const ContentDirectoryCatchUpPage = ContentDirectoryPage.extend({
    /** This recipient's wrap for each epoch introduced in (or current at) the returned revisions. */
    epochWraps: z
        .array(z.strictObject({ revision: Counter, epoch: Counter, wrap: ContentKeyWrap }))
        .max(64),
    /** Latest passkey-signed cumulative revocation list; endpoints verify it locally. */
    revocations: SignedRevocationListSchema.nullable(),
    /** Escrow public keys; accepted only when the thumbprint equals the signed directory keyId. */
    escrowKeys: z
        .array(z.strictObject({ recipientId: Id, kind: EscrowKind, publicKey: ContentPublicKey }))
        .max(8),
    /**
     * Browser pages only: passkey-signed endorsements of the devices that approved returned
     * snapshots (zero-tap machine grants). Endpoints verify them against their own pinned passkeys.
     */
    endorsements: z.array(DeviceEndorsement).max(16).optional(),
});
// ---- recovery requests: alert, hold and rate limit -------------------------------------------
export const RecoveryStatus = z.enum(['held', 'ready', 'denied', 'completed', 'expired']);
export const RecoveryRequest = z.strictObject({
    id: Id,
    kind: EscrowKind,
    recipientId: Id,
    status: RecoveryStatus,
    createdAt: Time,
    availableAt: Time,
    expiresAt: Time,
});
export const RecoveryStart = z.strictObject({ kind: EscrowKind, recipientId: Id.optional() });
export const RecoveryRef = z.strictObject({ requestId: Id });
export const RecoveryList = z.strictObject({ requests: z.array(RecoveryRequest).max(32) });
export const RecoveryBundle = z.strictObject({
    request: RecoveryRequest,
    escrow: DirectoryEscrowRecord,
    wraps: z.array(EscrowEpochWrap).max(4096),
});
export const RECOVERY_DECISION_DOMAIN = 'viko/recovery-decision/v1';
/** An existing endorsed device approves or denies a pending recovery (device ECDSA signature). */
export const RecoveryDecision = z.strictObject({
    statement: z.strictObject({
        version: z.literal(1),
        accountId: Id,
        requestId: Id,
        decision: z.enum(['approve', 'deny']),
        deviceKeyId: Id,
        issuedAt: Time,
    }),
    signature: ContentBase64Url.length(86),
    /**
     * Required to approve: a fresh passkey assertion over
     * SHA-256(RECOVERY_STEP_UP_DOMAIN || 0x00 || JCS(statement)), binding this request and device.
     */
    stepUp: WebAuthnAssertion.strict().optional(),
});
export const RECOVERY_STEP_UP_DOMAIN = 'viko/recovery-decision/step-up/v1';
// ---- revocation, session binding and daemon reports ------------------------------------------
export const SESSION_DEVICE_DOMAIN = 'viko/session-device/v1';
export const SessionBindChallenge = z.strictObject({ nonce: Digest });
/** Binds the current session to an endorsed device so revoking the device ends its sessions. */
export const SessionBind = z.strictObject({
    statement: z.strictObject({
        version: z.literal(1),
        accountId: Id,
        deviceKeyId: Id,
        nonce: Digest,
    }),
    signature: ContentBase64Url.length(86),
});
export const ContentRevoke = z.strictObject({ revocations: SignedRevocationListSchema });
export const ContentRevokeResult = z.strictObject({ seq: Counter, sessionsRevoked: Count });
export const SignedRevocationReportSchema = z.strictObject({
    report: z.strictObject({
        version: z.literal(1),
        accountId: Id,
        machineId: Id,
        daemonKeyId: Digest,
        revocationSeq: Count,
        directoryRevision: Count,
        epoch: Count,
        reportedAt: Time,
    }),
    signature: ContentBase64Url.length(86),
});
export const RevocationReportList = z.strictObject({
    reports: z
        .array(z.strictObject({ machineId: Id, signed: SignedRevocationReportSchema }))
        .max(256),
});
//# sourceMappingURL=content-keys.js.map